import { useMemo, useState, useCallback } from "react";
import {
  App as AntApp,
  message as staticMessage,
  Button,
  Card,
  Result,
  Segmented,
  Spin,
  Space,
  Tooltip,
} from "antd";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useTherapistDashboardController } from "./useTherapistDashboardController";
import { useAuth } from "../../../../hooks";
import { DashboardReviApi } from "../../../../api/revi/dashboardApi";
import {
  KpiCards,
  ChartsRow,
  RecentActivityTable,
  ClinicalProgressionSection,
  SesionesPorCategoriaLista,
  MotricityRecentCard,
} from "../../../../components/Admin/TherapistCmp/DashboardCmp";
import "./Page_Therapist_Dashboard.scss";
import { useMovil } from "../../../../hooks/useIsNarrow";

// El identificador del rango es tecnico (viaja al backend); lo que se traduce
// es la etiqueta que lee el terapeuta, asi que el mapa apunta a claves.
const TIME_RANGE_LABEL_KEY = {
  last_7_days: "dashboard.rangos.ultimos7dias",
  last_30_days: "dashboard.rangos.ultimos30dias",
  last_90_days: "dashboard.rangos.ultimos90dias",
  all_time: "dashboard.rangos.todoElHistorico",
};

export function Page_Therapist_Dashboard() {
  const movil = useMovil();
  // "Actualizar" sale del bloque comun: es la misma accion en todo el portal.
  const { t } = useTranslation(["therapist", "common"]);
  const appCtx = AntApp.useApp?.() || {};
  const appMessage = appCtx?.message;

  // ✅ Fallback seguro: si no hay App Provider, usa message estático (evita crash)
  const notify = useMemo(() => {
    return appMessage && typeof appMessage.success === "function"
      ? appMessage
      : staticMessage;
  }, [appMessage]);

  const { accessToken } = useAuth();
  const api = useMemo(() => new DashboardReviApi(), []);

  const {
    timeRange,
    setTimeRange,
    summary,
    charts,
    activity,
    loading,
    error,
    reload,
  } = useTherapistDashboardController();

  const [exporting, setExporting] = useState(false);

  // Salida de emergencia cuando el periodo elegido no tiene nada.
  //
  // El dashboard abre en "ultimos 30 dias" y en una plataforma que arranca -o
  // en un terapeuta que vuelve de vacaciones- eso deja tres paneles vacios a la
  // vez: las dos graficas y la actividad. Sin decir el periodo, los tres se
  // leen como "esto no funciona". Con el periodo y este boton, se leen como lo
  // que son y se arreglan de un clic.
  const verTodoElHistorico = useCallback(
    () => setTimeRange("all_time"),
    [setTimeRange],
  );

  const onExport = useCallback(async () => {
    if (!accessToken) {
      notify.warning(t("dashboard.exportar.sinToken"));
      return;
    }

    try {
      setExporting(true);

      const { blob, filename } = await api.exportEvaluationsCsv(accessToken, {
        timeRange,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "revi_evaluaciones.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      notify.success(t("dashboard.exportar.exito"));
    } catch (e) {
      console.error(e);
      // El mensaje del backend viene ya en su idioma: solo se traduce el
      // respaldo de cuando no llega ninguno.
      notify.error(e?.message || t("dashboard.exportar.error"));
    } finally {
      setExporting(false);
    }
  }, [accessToken, api, timeRange, notify, t]);

  return (
    <div className="revi-page-dashboard">
      <div className="revi-page-dashboard__main">
        <div className="revi-page-dashboard__header">
          <div className="revi-page-dashboard__title">
            <h2>{t("dashboard.titulo")}</h2>
            <p className="revi-subtitulo-pagina">{t("dashboard.subtitulo")}</p>
          </div>

          <div className="revi-page-dashboard__actions">
            <Segmented
              block={movil}
              value={timeRange}
              onChange={setTimeRange}
              options={[
                {
                  label: t("dashboard.selectorRango.dias7"),
                  value: "last_7_days",
                },
                {
                  label: t("dashboard.selectorRango.dias30"),
                  value: "last_30_days",
                },
                {
                  label: t("dashboard.selectorRango.dias90"),
                  value: "last_90_days",
                },
                { label: t("dashboard.selectorRango.todo"), value: "all_time" },
              ]}
            />

            <Space size={10} wrap>
              <Tooltip title={t("dashboard.exportar.ayuda")}>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={onExport}
                  loading={exporting}
                  className="revi-page-dashboard__export"
                >
                  {t("dashboard.exportar.boton")}
                </Button>
              </Tooltip>

              <Button icon={<ReloadOutlined />} onClick={reload}>
                {t("common:actions.update")}
              </Button>
            </Space>
          </div>
        </div>

        {error ? (
          <Card variant="outlined" className="revi-page-dashboard__error">
            <Result
              status="error"
              title={t("dashboard.errorCarga")}
              subTitle={error}
            />
          </Card>
        ) : (
          <>
            <KpiCards
              loading={loading}
              summary={summary}
              timeRangeLabel={t(TIME_RANGE_LABEL_KEY[timeRange])}
            />

            {/*
              El grafico de sectores solo entrega sus numeros al pasar el raton
              por encima, y en tactil ese gesto llega a la vez que el toque. En
              movil se sustituye por la lista, con las mismas cifras escritas.
            */}
            {movil ? (
              <SesionesPorCategoriaLista loading={loading} charts={charts} />
            ) : (
              <ChartsRow
                loading={loading}
                charts={charts}
                timeRangeLabel={t(TIME_RANGE_LABEL_KEY[timeRange])}
                onVerTodo={
                  timeRange === "all_time" ? undefined : verTodoElHistorico
                }
              />
            )}

            {/* La progresion clinica monta los mismos componentes que la ficha
                del paciente. En movil se llega a ellos desde la ficha, que es
                donde el terapeuta los busca. */}
            {!movil ? <ClinicalProgressionSection /> : null}

            <RecentActivityTable
              loading={loading}
              activity={activity}
              timeRangeLabel={t(TIME_RANGE_LABEL_KEY[timeRange])}
              onVerTodo={
                timeRange === "all_time" ? undefined : verTodoElHistorico
              }
            />

            <MotricityRecentCard />

            {loading && (
              <div className="revi-page-dashboard__overlay">
                <Spin size="large" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
